import { type FC, type HTMLAttributes, useCallback, useId, useState } from "react";

import { cn } from "../utils";

export type SwitchSize = "md" | "sm" | "xs" | "xxs";

export type SwitchProps = {
	"checked": boolean;
	"onChange"?: (checked: boolean) => void;
	"size"?: SwitchSize;
	"id"?: string;
	"disabled"?: boolean;
	"aria-label"?: string;
	"aria-labelledby"?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, "onChange">;

const sizeStyles: Record<SwitchSize, { track: string; thumb: string; translate: string }> = {
	md: {
		track: "w-[2.75rem] h-[1.5rem]",
		thumb: "h-[1.5rem] w-[1.5rem]",
		translate: "translate-x-[1.25rem]",
	},
	sm: {
		track: "w-[2.25rem] h-[1.25rem]",
		thumb: "h-[1.25rem] w-[1.25rem]",
		translate: "translate-x-[1rem]",
	},
	xs: {
		track: "w-[1.75rem] h-[1rem]",
		thumb: "h-[1rem] w-[1rem]",
		translate: "translate-x-[0.75rem]",
	},
	xxs: {
		track: "w-[1.5rem] h-[0.75rem]",
		thumb: "h-[0.75rem] w-[0.75rem]",
		translate: "translate-x-[0.75rem]",
	},
};

export const Switch: FC<SwitchProps> = ({
	checked,
	onChange,
	size = "md",
	"id": idProp,
	disabled = false,
	className,
	"aria-label": ariaLabel = "Toggle",
	"aria-labelledby": ariaLabelledBy,
	...props
}) => {
	const autoId = useId();
	const id = idProp ?? autoId;
	const [isFocused, setIsFocused] = useState(false);
	const styles = sizeStyles[size];

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onChange?.(e.target.checked);
		},
		[onChange]
	);

	return (
		<div
			className={cn(
				"relative inline-block cursor-pointer rounded-full transition-colors duration-200",
				styles.track,
				checked ? "bg-brand" : "bg-quaternary",
				isFocused && checked && "ring ring-normal border border-brand",
				disabled && "opacity-50 cursor-not-allowed",
				className
			)}
			{...props}
		>
			<input
				id={id}
				type="checkbox"
				role="switch"
				aria-checked={checked}
				aria-label={ariaLabelledBy ? undefined : ariaLabel}
				aria-labelledby={ariaLabelledBy}
				checked={checked}
				disabled={disabled}
				onChange={handleChange}
				onFocus={() => setIsFocused(true)}
				onBlur={() => setIsFocused(false)}
				className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
			/>
			<div
				aria-hidden
				className={cn(
					"pointer-events-none absolute top-1/2 left-0 rounded-full bg-primary drop-shadow-sm transition-transform duration-200",
					styles.thumb,
					"scale-[0.7] -translate-y-1/2",
					checked && styles.translate
				)}
			/>
		</div>
	);
};
